import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Card, CardBody, CardFooter, Col, Container, DropdownItem, DropdownMenu, DropdownToggle, Form, FormGroup, Input, Label, Row, Table, UncontrolledDropdown } from "reactstrap";
import { toast } from "react-toastify";

import { confirm } from "components/Confirmation";

import IconButton from "components/IconButton";
import CustomPagination from "components/CustomPagination";

import cruzamentoService from "services/CruzamentoService";
import relatorioService from "services/RelatorioService";
import ModalAvisosRelatorio from "components/ModalAvisosRelatorio";

import AppContext from "AppContext";
import { PapelEnum } from "services/PerfilService";
import { GFComboField } from "components/GeneralForm";
import comboService from "services/ComboService";
import { TipoContratoEnum } from "services/ContratoService";

export default function Cruzamentos() {
  const { state } = useContext(AppContext)
  const [pagina, setPagina] = useState(1)
  const [paginas, setPaginas] = useState(0)
  const [termo, setTermo] = useState(null)
  const [items, setItems] = useState([])
  const [contratos, setContratos] = useState([])
  const [idContrato, setIdContrato] = useState(
    state?.contrato?.id
  )
  const [baixandoRelatorio, setBaixandoRelatorio] = useState(false)
  const [relatorioComAvisos, setRelatorioComAvisos] = useState(false)
  const navigate = useNavigate()
  const idTipoPerfil = state?.user?.perfil?.idTipo

  const podeGerenciar = useMemo(() => {
    return (state?.user?.acoes || []).includes('cruzamento.gerenciar')
  }, [state])

  const carregar = async () => {
    const res = await cruzamentoService.get(idContrato, termo, pagina)
    if (res.success) {
      setItems(res.data.items)
      setPaginas(res.data.paginas)
    }
  }

  useEffect(() => {
    carregar()

    if (idTipoPerfil == PapelEnum.ADMIN) {
      comboService.contrato().then(res => {
        if (res.success) {
          const contratos = res.data.filter(el => el.tipoContrato == TipoContratoEnum.SEMAFORICO)
          setContratos(contratos)
        }
      })
    }
  }, [])

  useEffect(() => {
    carregar()
  }, [pagina])

  const handleDelete = (item) => {
    confirm({
      header: "Exclusão",
      text: "Tem certeza que gostaria de continuar?",
      icon: "bi-exclamation-triangle",
      onConfirm: async () => {
        try {
          const res = await cruzamentoService.delete(state.contrato.id, item.id)
          if (res.success) {
            carregar()
            toast("Cruzamento excluído")
          } else {
            toast("Erro ao excluir o cruzamento")
          }
        } catch (ex) {
          toast("Erro ao excluir o cruzamento")
        }
      }
    })
  }

  const handleSubmit = e => {
    e.preventDefault()
    setPagina(1)
    carregar()
  }

  const downloadRelatorioCruzamento = async (idCruzamento) => {
    try {
      setBaixandoRelatorio(true)
      const avisos = await relatorioService.gerarPorCruzamento(idContrato, idCruzamento)
      setBaixandoRelatorio(false)
      if (avisos > 0)
        setRelatorioComAvisos(true)
    } catch (ex) {
      setBaixandoRelatorio(false)
      if (ex.status === 422) {
        if (ex.response?.data instanceof Blob) {
          const reader = new FileReader();
          reader.onload = () => {
            const result = JSON.parse(reader.result)
            if (result.message)
              toast(result.message)
            else
              toast("Erro inesperado ao baixar relatório")
          };
          reader.readAsText(ex.response.data);
        } else {
          toast("Erro inesperado ao baixar relatório")
        }
      } else {
        toast("Erro inesperado ao baixar relatório")
      }
    }
  }

  return (
    <>
      <Container fluid>
        <div className="d-flex flex-row justify-content-between align-items-center">
          <h1 className="my-4">Cruzamentos</h1>
          {podeGerenciar && (
            <Link className="btn btn-primary btn-icon" to="/admin/cruzamentos/0">Incluir</Link>
          )}
        </div>

        <Card className="shadow mb-4">
          <CardBody>
            <Form onSubmit={handleSubmit}>
              <Row>
                {(idTipoPerfil == PapelEnum.ADMIN) && (
                  <GFComboField
                    colspan={3}
                    label="Contrato"
                    name="idContrato"
                    data={{ idContrato }}
                    onChange={(name, value) => setIdContrato(value)}
                    options={contratos}
                    optionValue="id"
                    optionLabel="numero"
                    showDefault
                    defaultValue="Todas"
                  />
                )}
                <Col>
                  <FormGroup>
                    <Label>Número</Label>
                    <Input type="text" placeholder="Número" value={termo || ''} onChange={e => setTermo(e.target.value)} />
                  </FormGroup>
                </Col>
                <Col className="d-flex align-items-end justify-content-end">
                  <FormGroup>
                    <Button type="submit" color="primary">Buscar</Button>
                  </FormGroup>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>

        <Card className="shadow">
          <Table className="align-items-center table-flush" responsive>
            <thead className="thead-light">
              <tr>
                <th scope="col">Contrato</th>
                <th scope="col">Número</th>
                <th scope="col">Endereço</th>
                <th scope="col">Controlado por</th>
                <th scope="col" style={{ width: 120 }}></th>
              </tr>
            </thead>
            <tbody>
              {items.length == 0 && (
                <tr>
                  <td colSpan={5} className="text-center">Nenhum registro</td>
                </tr>
              )}
              {items.map(el => (
                <tr key={el.id}>
                  <td>{el.contrato}</td>
                  <td>{el.numero}</td>
                  <td>{el.endereco}</td>
                  <td>{el.cruzamentoMestre?.numero || '-'}</td>
                  <td className="text-right">
                    <UncontrolledDropdown>
                      <DropdownToggle data-toggle="dropdown" tag="span">
                        <IconButton alt="Ações" iconClass="bi-three-dots-vertical" />
                      </DropdownToggle>
                      <DropdownMenu right>
                        <DropdownItem onClick={() => navigate("/admin/cruzamentos/" + el.id)}>
                          Editar
                        </DropdownItem>
                        {podeGerenciar && (
                          <DropdownItem onClick={() => handleDelete(el)}>
                            Excluir
                          </DropdownItem>
                        )}
                        <DropdownItem onClick={() => downloadRelatorioCruzamento(el.id)} disabled={baixandoRelatorio}>
                          {baixandoRelatorio ? "Baixando..." : "Baixar relatório"}
                        </DropdownItem>
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <CardFooter className="py-4">
            <CustomPagination pagina={pagina} paginas={paginas} onChange={setPagina} />
          </CardFooter>
        </Card>
        <ModalAvisosRelatorio isOpen={relatorioComAvisos} onClosed={() => setRelatorioComAvisos(false)} />
      </Container>
    </>
  )
}